import { useUpdateAssessmentDate } from "@/api/console/companies/useUpdateAssessmentDate";
import DatePicker from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { handleCompanyName } from "@/lib/handleName";
import toast from "@/lib/toast";
import { AssessmentDate } from "@/schema/console/companies/AssessmentDate";
import useSelectedCompany from "@/src/console/(auth)/companies/_hooks/useCompanyDialog";
import { SpinnerIosRegular } from "@fluentui/react-icons";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { mutate } from "swr";

function UpdateAssessmentDateDialog() {
  const { t } = useTranslation();
  const { type, resetDialog, company } = useSelectedCompany();

  const companyId = company?.id ?? -1;
  const companyName = handleCompanyName(companyId, company?.company_name ?? "");
  const [selectedDate, setSelectedDate] = useState<AssessmentDate>({});

  useEffect(() => {
    setSelectedDate({
      start_date: company?.start_date,
      end_date: company?.end_date,
    });
  }, [company]);

  const { trigger: updateAssessmentDate, isMutating } = useUpdateAssessmentDate(
    companyId,
    {
      onSuccess: (data) => {
        toast.success(data.message);
        mutate(`/console/companies`);
        resetDialog();
      },
    },
  );

  return (
    <Dialog
      open={type === "update-assessment-date"}
      onOpenChange={(open) => !open && resetDialog()}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("update-assessment-date")}</DialogTitle>
        </DialogHeader>
        <div className=" flex flex-col p-6">
          <div>
            <div className=" text-lg font-bold">{companyName}</div>
          </div>
          <div className=" mt-6">
            <div className=" text-sm font-semibold mb-2">{t("start-date")}</div>
            <DatePicker
              value={
                selectedDate.start_date
                  ? new Date(selectedDate.start_date)
                  : undefined
              }
              onDateChange={(start_date) =>
                setSelectedDate({
                  ...selectedDate,
                  start_date: start_date
                    ? format(start_date, "yyyy-MM-dd")
                    : undefined,
                })
              }
            />
          </div>
          <div className=" mt-6">
            <div className=" text-sm font-semibold mb-2">{t("end-date")}</div>
            <DatePicker
              value={
                selectedDate.end_date
                  ? new Date(selectedDate.end_date)
                  : undefined
              }
              onDateChange={(end_date) =>
                setSelectedDate({
                  ...selectedDate,
                  end_date: end_date
                    ? format(end_date, "yyyy-MM-dd")
                    : undefined,
                })
              }
            />
          </div>
          <div className=" flex flex-row w-full justify-end mt-6 space-x-3">
            <Button onClick={() => resetDialog()} variant={"outline"}>
              {t("cancel")}
            </Button>
            <Button
              onClick={() => updateAssessmentDate(selectedDate)}
              LoadingComponent={<SpinnerIosRegular className="animate-spin" />}
              isLoading={isMutating}
            >
              {t("update")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default UpdateAssessmentDateDialog;
